import { Box, Container, Typography, Button, Stack, keyframes } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

function FinalCTA() {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();

  const handleGetStarted = () => {
    keycloak.register();
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        background: `
          linear-gradient(135deg,
            rgba(144, 202, 249, 0.15) 0%,
            rgba(244, 143, 177, 0.10) 50%,
            rgba(144, 202, 249, 0.15) 100%
          )
        `,
        backgroundSize: '200% 200%',
        animation: `${gradientShift} 15s ease infinite`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(ellipse at top left, rgba(144, 202, 249, 0.2), transparent 50%),
            radial-gradient(ellipse at bottom right, rgba(244, 143, 177, 0.2), transparent 50%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Floating decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 60,
          height: 60,
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          border: '2px solid rgba(144, 202, 249, 0.3)',
          animation: `${float} 6s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '25%',
          right: '15%',
          width: 80,
          height: 80,
          borderRadius: '70% 30% 50% 50% / 50% 60% 40% 50%',
          border: '2px solid rgba(244, 143, 177, 0.3)',
          animation: `${float} 8s ease-in-out infinite`,
          animationDelay: '-3s',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, px: { xs: 3, sm: 3, md: 3 } }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 2,
              color: '#FFFFFF',
            }}
          >
            {t('landing.finalCta.title')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#ADB5BD',
              mb: 5,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            {t('landing.finalCta.subtitle')}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="outlined"
              size="large"
              onClick={handleGetStarted}
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 2,
                px: 5,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                borderWidth: 2,
                borderColor: '#90caf9',
                color: '#90caf9',
                background: 'rgba(144, 202, 249, 0.05)',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#42a5f5',
                  borderWidth: 2,
                  background: 'rgba(144, 202, 249, 0.12)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 16px 48px rgba(144, 202, 249, 0.3)',
                  '& .MuiButton-endIcon': {
                    transform: 'translateX(4px)',
                  },
                },
                '& .MuiButton-endIcon': {
                  transition: 'transform 0.3s ease',
                },
              }}
            >
              {t('landing.finalCta.cta')}
            </Button>
          </Stack>

          {/* Trust indicators */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="center"
            sx={{
              mt: 6,
              pt: 5,
              borderTop: '1px solid rgba(144, 202, 249, 0.1)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#66bb6a',
                  display: 'inline-block',
                }}
              />
              {t('landing.finalCta.noCreditCard')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#66bb6a',
                  display: 'inline-block',
                }}
              />
              {t('landing.finalCta.freeForever')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#ADB5BD', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#66bb6a',
                  display: 'inline-block',
                }}
              />
              {t('landing.finalCta.upgradeAnytime')}
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default FinalCTA;
